import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Restaurant } from '@/queries/useRestaurants'
import { useTranslation } from 'react-i18next'

interface DeleteRestaurantDialogProps {
  restaurant: Restaurant | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void | Promise<void>
  isLoading?: boolean
  error?: Error | null
}

export default function DeleteRestaurantDialog({
  restaurant,
  open,
  onOpenChange,
  onConfirm,
  isLoading = false,
  error,
}: DeleteRestaurantDialogProps) {
  const { t } = useTranslation('admin')
  
  const handleConfirm = async () => {
    await onConfirm()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="delete-restaurant-dialog">
        <DialogHeader>
          <DialogTitle data-testid="delete-restaurant-dialog-title">
            {t('restaurants.dialog.deleteTitle')}
          </DialogTitle>
          <DialogDescription 
            data-testid="delete-restaurant-dialog-description"
            dangerouslySetInnerHTML={{
              __html: t('restaurants.dialog.deleteDescription', { name: restaurant?.name || '' })
            }}
          />
        </DialogHeader>

        {error && (
          <Alert variant="destructive" data-testid="delete-restaurant-dialog-error">
            <AlertDescription>
              {error.message || t('restaurants.dialog.deleteError')}
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            data-testid="delete-restaurant-dialog-cancel-button"
          >
            {t('restaurants.form.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isLoading}
            data-testid="delete-restaurant-dialog-confirm-button"
          >
            {isLoading ? t('common:buttons.deleting', { defaultValue: 'Deleting...' }) : t('common:buttons.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

